import {
  ColumnData,
  SlideAction,
  SlideLayout,
  useSlideLayoutSafe,
  ViewMode,
} from '@gripp/shared-ui';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { NewAlertHeaderAction } from '../../shared/components/alerts/newAlertHeaderAction';
import { RoutineSubmitSource } from '../assetUtils';
import { AlertPanel } from './alert-panel';
import { AssetRoutineSubmitColumn } from './asset-routine-submit-column';
import { AssetRoutineSubmitHeader } from './asset-routine-submit-header';

export const AssetRoutineAlerts = () => {
  const { assetId, routineId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const slideLayout = useSlideLayoutSafe();
  const { t } = useTranslation();
  const backGuardRef = useRef<
    ((onProceed: () => void, onCancel?: () => void) => void) | null
  >(null);

  if (!assetId || !routineId) {
    return null;
  }

  const isAlertPage = location.pathname.includes('/alerts');

  const from: RoutineSubmitSource =
    location.state?.from ?? RoutineSubmitSource.AssetCard;

  const routineUrl = `/assets/${assetId}/routines/${routineId}`;

  const backButtonGuard = (): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      if (!backGuardRef.current) {
        resolve(true);
        return;
      }
      backGuardRef.current(
        () => resolve(true),
        () => resolve(false)
      );
    });
  };

  const leftColumn: ColumnData = {
    title: 'routines.editRoutines.editRoutinesButton',
    mainComponent: (
      <AssetRoutineSubmitColumn
        assetId={assetId}
        backGuardRef={backGuardRef}
      />
    ),
    showBackButton: true,
    disableScroll: true,
    headerComponent: (
      <AssetRoutineSubmitHeader assetId={assetId} routineId={routineId} />
    ),
    backButtonTransition: SlideAction.PREV,
    backButtonGuard,
    onBackButtonPress: () => navigate(routineUrl, { state: { from } }),
  };

  const rightColumn: ColumnData = {
    title: 'alerts.alerts',
    mainComponent: (
      <AlertPanel
        assetId={assetId}
        routineId={routineId}
      />
    ),
    headerComponent: (
      <NewAlertHeaderAction
        assetId={assetId}
        routineId={routineId}
        accessibilityLabel={t('alerts.setAlert')}
        onBeforeNavigate={async () => {
          if (slideLayout && !isAlertPage) {
            await slideLayout.onSlideChange(SlideAction.NEXT);
          }
        }}
      />
    ),
    backButtonTransition: SlideAction.PREV,
    onBackButtonPress: () => navigate(routineUrl, { state: { from } }),
    showBackButton: true,
    showHeader: true,
    disableScroll: true,
  };

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={rightColumn}
      viewMode={ViewMode.DUAL}
    />
  );
};
